import React, {useState} from 'react';

import {USER_CREATED_COLUMN_NAME_MAX_LENGTH} from 'notebook/tables/constants/TableColumnConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';

import {CustomColumnDataType, getCustomColumnDataTypeDisplayName} from './CustomColumnConstants';

export interface CustomColumnConfigModalProps {
  onRequestSubmit(result: {name: string; dataType: CustomColumnDataType}): Promise<void>;
  onRequestClose(): void;
}

const NAME_TOO_LONG_ERROR_MESSAGE = `Name must not exceed ${USER_CREATED_COLUMN_NAME_MAX_LENGTH} characters in length.`;
const isNameTooLong = (name: string): boolean => name.length > USER_CREATED_COLUMN_NAME_MAX_LENGTH;

function CustomColumnConfigModal(props: CustomColumnConfigModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [dataType, setDataType] = useState(CustomColumnDataType.TEXT);

  return (
    <Modal
      title="Insert Custom Column"
      onRequestClose={() => {
        props.onRequestClose();
      }}
      onRequestSubmit={() => {
        props.onRequestSubmit({
          name,
          dataType,
        });
      }}
      buttons={[
        {
          text: 'Insert',
          submit: true,
          disabled: !name.length || isNameTooLong(name),
          dataTestAttrs: {component: 'CustomColumnConfigModal', element: 'insertButton'},
        },
      ]}
      dataTestAttrs={{component: 'CustomColumnConfigModal', element: 'modal'}}
    >
      <Row>
        <Col md={12}>Data in custom columns won’t be synced with the Benchling data warehouse.</Col>
        <br />
      </Row>
      <Row>
        <Col md={12}>
          <FormGroup dataTestAttrs={{component: 'CustomColumnConfigModal', element: 'nameInput'}}>
            <Label>Column Name</Label>
            <FormInput
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              error={isNameTooLong(name) ? NAME_TOO_LONG_ERROR_MESSAGE : ''}
            />
            {isNameTooLong(name) && <ErrorMessage error={NAME_TOO_LONG_ERROR_MESSAGE} />}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <FormGroup>
            <Label>Data Type</Label>
            <SelectDropdown
              menu={
                <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} value={dataType}>
                  {Object.values(CustomColumnDataType).map((optionDataType: CustomColumnDataType) => (
                    <Option
                      value={optionDataType}
                      onSelect={() => {
                        setDataType(optionDataType);
                      }}
                      key={optionDataType}
                    >
                      {getCustomColumnDataTypeDisplayName(optionDataType)}
                    </Option>
                  ))}
                </Menu>
              }
              displayName={getCustomColumnDataTypeDisplayName(dataType)}
              dataTestAttrs={{component: 'CustomColumnConfigModal', element: 'dataTypeInput'}}
            />
          </FormGroup>
        </Col>
      </Row>
    </Modal>
  );
}

export default CustomColumnConfigModal;
